import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { API_URL } from '../lib/supabase';
import { viewProviderDocument } from '../lib/documents';
import { formatDateNZ, formatDateTimeNZ } from '../lib/format';
import {
  ChevronLeft, CheckCircle, XCircle, Clock, Users, FileText, Shield,
  MapPin, Briefcase, Mail, Phone, Star, ChevronRight, AlertCircle, Search,
  Plus, Edit2, Eye, EyeOff, GripVertical, Layers, CreditCard, MessageSquare,
  Trash2, BarChart3, AlertTriangle, Pause
} from 'lucide-react';

const STATUS_STYLES = {
  pending: { bg: 'rgba(245,158,11,0.1)', color: '#D97706', label: 'Pending Review' },
  approved: { bg: 'rgba(34,197,94,0.1)', color: '#16A34A', label: 'Approved' },
  rejected: { bg: 'rgba(239,68,68,0.1)', color: '#DC2626', label: 'Rejected' },
  suspended: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: 'Suspended' },
  incomplete_onboarding: { bg: 'rgba(245,158,11,0.12)', color: '#B45309', label: 'Onboarding not started' },
};

const SUB_STATUS_STYLES = {
  active: { bg: 'rgba(34,197,94,0.1)', color: '#16A34A', label: 'Active' },
  trial: { bg: 'rgba(59,130,246,0.1)', color: '#2563EB', label: 'Trial' },
  past_due: { bg: 'rgba(239,68,68,0.1)', color: '#DC2626', label: 'Past Due' },
  canceled: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: 'Canceled' },
  expired: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: 'Expired' },
};

const REVIEW_STATUS_STYLES = {
  published: { bg: 'rgba(34,197,94,0.1)', color: '#16A34A', label: 'Published' },
  hidden: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: 'Hidden' },
  flagged: { bg: 'rgba(245,158,11,0.1)', color: '#D97706', label: 'Flagged' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span data-testid={`status-${status}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '50px', fontSize: '0.75rem', fontWeight: 600, background: s.bg, color: s.color }}>
      {status === 'approved' && <CheckCircle size={12} />}
      {status === 'rejected' && <XCircle size={12} />}
      {status === 'pending' && <Clock size={12} />}
      {status === 'incomplete_onboarding' && <AlertCircle size={12} />}
      {s.label}
    </span>
  );
};

// "Last reminded 3d ago" / "Reminded today" / "" if never
const formatLastReminder = (iso) => {
  if (!iso) return '';
  try {
    const sent = new Date(iso);
    const now = new Date();
    const diffMs = now - sent;
    if (diffMs < 0) return '';
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Reminded today';
    if (days === 1) return 'Reminded yesterday';
    if (days < 30) return `Reminded ${days}d ago`;
    if (days < 365) return `Reminded ${Math.floor(days / 30)}mo ago`;
    return `Reminded ${Math.floor(days / 365)}y ago`;
  } catch { return ''; }
};

// ============================================
// ADMIN PROVIDER LIST PAGE
// ============================================
export const AdminProviderListPage = () => {
  const { session } = useAuth();
  const { country } = useCountry();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [reminderInFlight, setReminderInFlight] = useState({});

  useEffect(() => {
    if (session?.access_token) fetchProviders();
  }, [session, statusFilter]);

  const fetchProviders = async () => {
    try {
      let url = `${API_URL}/admin/providers`;
      if (statusFilter) url += `?status=${statusFilter}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.ok) setProviders(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSuspend = async (providerId, name) => {
    if (!window.confirm(`Suspend "${name}"? They will be hidden from public listings.`)) return;
    try {
      const res = await fetch(`${API_URL}/admin/providers/${providerId}/suspend`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) { setLoading(true); fetchProviders(); }
      else alert('Suspend failed.');
    } catch (err) { console.error(err); }
  };

  const handleReactivate = async (providerId, name) => {
    if (!window.confirm(`Reactivate "${name}" and make them visible again?`)) return;
    try {
      const res = await fetch(`${API_URL}/admin/providers/${providerId}/reactivate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) { setLoading(true); fetchProviders(); }
      else {
        let body = null; try { body = await res.json(); } catch {}
        alert(body?.detail || `Reactivate failed (HTTP ${res.status}).`);
      }
    } catch (err) { console.error(err); alert('Network error during reactivate.'); }
  };

  // Email preview modal state: holds the rendered email + recipient/sender/subject.
  // When set, the modal opens. confirmed Send fires the actual reminder POST.
  const [reminderPreview, setReminderPreview] = useState(null);
  // { profileId, to, to_name, subject, sender, html }
  const [reminderPreviewLoading, setReminderPreviewLoading] = useState(false);
  const [reminderPreviewError, setReminderPreviewError] = useState('');

  const fetchReminderPreview = async (profileId) => {
    setReminderPreviewLoading(true);
    setReminderPreviewError('');
    try {
      const res = await fetch(`${API_URL}/admin/providers/incomplete/${profileId}/preview-reminder`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReminderPreview({ profileId, ...data });
      } else {
        let body = null; try { body = await res.json(); } catch {}
        setReminderPreviewError(body?.detail || `Could not load preview (HTTP ${res.status}).`);
        setReminderPreview({ profileId, error: true });
      }
    } catch (err) {
      console.error(err);
      setReminderPreviewError('Network error loading preview.');
      setReminderPreview({ profileId, error: true });
    } finally {
      setReminderPreviewLoading(false);
    }
  };

  // Eye icon → preview-only modal (no send button shown unless admin opts in).
  const handlePreviewReminder = (profileId) => {
    fetchReminderPreview(profileId);
  };

  // Send button → open preview-then-confirm modal. Same fetch; modal shows
  // a primary 'Send Now' button.
  const handleSendReminder = (profileId) => {
    fetchReminderPreview(profileId);
  };

  const handleSendReminderConfirmed = async () => {
    if (!reminderPreview || reminderPreview.error) return;
    const profileId = reminderPreview.profileId;
    const email = reminderPreview.to;
    setReminderInFlight(prev => ({ ...prev, [profileId]: true }));
    try {
      const res = await fetch(`${API_URL}/admin/providers/incomplete/${profileId}/send-reminder`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        setReminderPreview(null);
        alert(`Reminder email sent to ${email}`);
        // Refresh list to show updated "Last reminded" timestamp
        setLoading(true);
        fetchProviders();
      } else {
        let body = null; try { body = await res.json(); } catch {}
        setReminderPreviewError(body?.detail || `Failed to send reminder (HTTP ${res.status}).`);
      }
    } catch (err) {
      console.error(err);
      setReminderPreviewError('Network error sending reminder.');
    } finally {
      setReminderInFlight(prev => { const n = { ...prev }; delete n[profileId]; return n; });
    }
  };

  const handleBulkApproveDocs = async (providerId, name, pendingCount) => {
    if (!window.confirm(`Approve ${pendingCount} pending document${pendingCount > 1 ? 's' : ''} for "${name}"? This will also update their verification badges.`)) return;
    try {
      const res = await fetch(`${API_URL}/admin/providers/${providerId}/approve-pending-documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) { setLoading(true); fetchProviders(); }
      else {
        let body = null; try { body = await res.json(); } catch {}
        alert(body?.detail || `Bulk approve failed (HTTP ${res.status}).`);
      }
    } catch (err) { console.error(err); alert('Network error during bulk approve.'); }
  };

  const filtered = providers.filter(p => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return p.business_name?.toLowerCase().includes(q)
      || p.email?.toLowerCase().includes(q)
      || p.full_name?.toLowerCase().includes(q)
      || p.city?.toLowerCase().includes(q);
  });

  const counts = {
    all: providers.length,
    incomplete: providers.filter(p => p.status === 'incomplete_onboarding').length,
    pending: providers.filter(p => p.status === 'pending').length,
    approved: providers.filter(p => p.status === 'approved').length,
    rejected: providers.filter(p => p.status === 'rejected').length,
    suspended: providers.filter(p => p.status === 'suspended').length,
  };

  // Stale = signed up >60 days ago, still incomplete
  const STALE_THRESHOLD_MS = 60 * 24 * 60 * 60 * 1000;
  const isStale = (p) => p.is_incomplete_onboarding
    && p.created_at
    && (Date.now() - new Date(p.created_at).getTime() > STALE_THRESHOLD_MS);

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page admin-page" data-testid="admin-providers-page">
      <div className="page-header">
        <h1>Provider Management</h1>
        <p>
          {counts.pending > 0 && `${counts.pending} provider${counts.pending > 1 ? 's' : ''} awaiting review`}
          {counts.pending > 0 && counts.incomplete > 0 && ' · '}
          {counts.incomplete > 0 && `${counts.incomplete} incomplete onboarding`}
          {counts.pending === 0 && counts.incomplete === 0 && 'All providers reviewed'}
        </p>
      </div>

      {/* Status counts */}
      <div className="admin-stat-row" data-testid="admin-stats">
        {[
          { key: '', label: 'All', count: counts.all, icon: <Users size={18} /> },
          { key: 'incomplete_onboarding', label: 'Incomplete Onboarding', count: counts.incomplete, icon: <AlertCircle size={18} /> },
          { key: 'pending', label: 'Pending', count: counts.pending, icon: <Clock size={18} /> },
          { key: 'approved', label: 'Approved', count: counts.approved, icon: <CheckCircle size={18} /> },
          { key: 'suspended', label: 'Suspended', count: counts.suspended, icon: <Pause size={18} /> },
          { key: 'rejected', label: 'Rejected', count: counts.rejected, icon: <XCircle size={18} /> },
        ].map(s => (
          <button
            key={s.key || 'all'}
            className={`admin-stat-btn ${statusFilter === s.key ? 'active' : ''}`}
            onClick={() => { setStatusFilter(s.key); setLoading(true); }}
            data-testid={`filter-${s.key || 'all'}`}
          >
            {s.icon}
            <span className="admin-stat-count">{s.count}</span>
            <span className="admin-stat-label">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="admin-search-bar">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search by business name, email, or city..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          data-testid="admin-search"
        />
      </div>

      {/* Providers table */}
      <div className="admin-table-container" data-testid="providers-table">
        {filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Users size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
            <p>No providers found</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Business</th>
                <th>Location</th>
                <th>Services</th>
                <th>Docs</th>
                <th>Subscription</th>
                <th>Trial Ends</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} data-testid={`provider-row-${p.id}`} className={p.is_incomplete_onboarding ? 'admin-row-incomplete' : ''}>
                  <td>
                    <div className="admin-cell-primary">
                      {p.business_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(not yet entered)</span>}
                      {isStale(p) && (
                        <span
                          data-testid={`stale-badge-${p.id}`}
                          title="Signed up more than 60 days ago and never finished onboarding"
                          style={{ marginLeft: 8, padding: '2px 8px', background: 'rgba(245,158,11,0.2)', color: '#B45309', borderRadius: '50px', fontSize: '0.6875rem', fontWeight: 700 }}
                        >
                          Stale (60d+)
                        </span>
                      )}
                    </div>
                    <div className="admin-cell-secondary">
                      {p.full_name || '—'} &middot; {p.email}
                      {p.phone && <> &middot; {p.phone}</>}
                    </div>
                  </td>
                  <td>
                    {p.is_incomplete_onboarding
                      ? <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>
                      : <><MapPin size={13} /> {p.city || '—'}, {p.country_code}</>}
                  </td>
                  <td style={{ textAlign: 'center' }}>{p.services_count}</td>
                  <td style={{ textAlign: 'center' }} data-testid={`docs-cell-${p.id}`}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span>{p.docs_count}</span>
                      {p.pending_docs_count > 0 && (
                        <span
                          title={`${p.pending_docs_count} pending review`}
                          style={{ padding: '2px 8px', background: 'rgba(245,158,11,0.15)', color: '#D97706', borderRadius: '50px', fontSize: '0.6875rem', fontWeight: 700 }}
                          data-testid={`pending-docs-badge-${p.id}`}
                        >
                          {p.pending_docs_count} pending
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{p.subscription ? <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent)' }}>{p.subscription.status} / {p.subscription.plan}</span> : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>None</span>}</td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }} data-testid={`trial-end-${p.id}`}>
                    {p.subscription?.trial_end ? formatDateNZ(p.subscription.trial_end) : '—'}
                  </td>
                  <td><StatusBadge status={p.status} /></td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {p.is_incomplete_onboarding ? (
                          <a
                            href={`mailto:${p.email}?subject=${encodeURIComponent('Complete your HandyHub provider onboarding')}&body=${encodeURIComponent(`Hi ${p.full_name || 'there'},\n\nWe noticed you signed up to HandyHub as a service provider but haven't finished setting up your business profile yet. You can complete it here: https://${country.domain}/provider/onboarding\n\nLet me know if you have any questions.\n\nThanks,\nThe HandyHub Team`)}`}
                            data-testid={`contact-${p.id}`}
                            title={`Email ${p.email}`}
                            style={{ padding: '6px 10px', background: '#fff', border: '1px solid var(--border-color)', color: 'var(--text-dark)', borderRadius: 'var(--radius-sm)', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            <Mail size={12} /> Contact
                          </a>
                        ) : (
                          <>
                            <Link to={`/admin/providers/${p.id}`} className="admin-review-btn" data-testid={`review-${p.id}`}>
                              Review <ChevronRight size={14} />
                            </Link>
                            {p.status === 'approved' && (
                              <button
                                onClick={() => handleSuspend(p.id, p.business_name)}
                                data-testid={`suspend-${p.id}`}
                                title="Suspend provider"
                                style={{ padding: '6px 10px', background: '#fff', border: '1px solid #DC2626', color: '#DC2626', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              >
                                <Pause size={12} /> Suspend
                              </button>
                            )}
                            {p.status === 'suspended' && (
                              <button
                                onClick={() => handleReactivate(p.id, p.business_name)}
                                data-testid={`reactivate-${p.id}`}
                                title="Reactivate provider"
                                style={{ padding: '6px 10px', background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              >
                                <CheckCircle size={12} /> Reactivate
                              </button>
                            )}
                            {p.status === 'rejected' && (
                              <button
                                onClick={() => handleReactivate(p.id, p.business_name)}
                                data-testid={`reconsider-${p.id}`}
                                title="Move back to Pending review"
                                style={{ padding: '6px 10px', background: '#fff', border: '1px solid var(--border-color)', color: 'var(--text-dark)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              >
                                <Clock size={12} /> Reconsider
                              </button>
                            )}
                            {p.pending_docs_count > 0 && p.status !== 'pending' && (
                              <button
                                onClick={() => handleBulkApproveDocs(p.id, p.business_name, p.pending_docs_count)}
                                data-testid={`approve-pending-docs-${p.id}`}
                                title={`Approve ${p.pending_docs_count} pending document(s)`}
                                style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.1)', border: '1px solid #16A34A', color: '#16A34A', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              >
                                <FileText size={12} /> Approve Docs ({p.pending_docs_count})
                              </button>
                            )}
                          </>
                        )}

                        {/* Reminder buttons — surfaced whenever backend says can_remind */}
                        {p.can_remind && (
                          <>
                            <button
                              onClick={() => handlePreviewReminder(p.profile_id || p.id)}
                              data-testid={`preview-reminder-${p.id}`}
                              title={p.is_pending_review ? 'Preview "under review" email' : `Preview the reminder email${p.missing_items && p.missing_items.length ? ` (${p.missing_items.length} item${p.missing_items.length > 1 ? 's' : ''} missing)` : ''}`}
                              style={{ padding: '6px 10px', background: '#fff', border: '1px solid var(--accent)', color: 'var(--accent)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <Eye size={12} /> Preview Email
                            </button>
                            <button
                              onClick={() => handleSendReminder(p.profile_id || p.id)}
                              disabled={!!reminderInFlight[p.profile_id || p.id]}
                              data-testid={`send-reminder-${p.id}`}
                              title={p.is_pending_review ? 'Send "under review" check-in email' : 'Send reminder email for missing items'}
                              style={{ padding: '6px 10px', background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)', cursor: reminderInFlight[p.profile_id || p.id] ? 'wait' : 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: reminderInFlight[p.profile_id || p.id] ? 0.6 : 1 }}
                            >
                              {p.is_pending_review ? <Clock size={12} /> : <AlertCircle size={12} />}
                              {reminderInFlight[p.profile_id || p.id]
                                ? 'Sending…'
                                : (p.is_pending_review ? 'Send Check-in' : 'Send Reminder')}
                            </button>
                          </>
                        )}
                      </div>

                      {/* Last reminder timestamp + missing-items hint */}
                      {p.can_remind && (p.last_reminder_sent_at || (p.missing_items && p.missing_items.length > 0)) && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                          {p.last_reminder_sent_at && (
                            <span data-testid={`last-reminded-${p.id}`} title={p.last_reminder_sent_at} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                              <Clock size={10} /> {formatLastReminder(p.last_reminder_sent_at)}
                            </span>
                          )}
                          {p.missing_items && p.missing_items.length > 0 && !p.is_pending_review && (
                            <span data-testid={`missing-count-${p.id}`} style={{ padding: '1px 6px', background: 'rgba(245,158,11,0.12)', color: '#B45309', borderRadius: '50px', fontWeight: 700 }}>
                              {p.missing_items.length} item{p.missing_items.length > 1 ? 's' : ''} missing
                            </span>
                          )}
                          {p.is_pending_review && (
                            <span data-testid={`pending-review-pill-${p.id}`} style={{ padding: '1px 6px', background: 'rgba(59,130,246,0.12)', color: '#1D4ED8', borderRadius: '50px', fontWeight: 700 }}>
                              Under review
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Email Preview Modal ─────────────────────────────────────── */}
      {reminderPreview && (
        <div
          className="modal-backdrop"
          data-testid="reminder-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setReminderPreview(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}
        >
          <div
            className="modal-card"
            style={{ background: '#fff', width: '100%', maxWidth: '720px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.35)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700, color: 'var(--text-dark)' }}>Reminder Email Preview</h2>
                <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Review the exact email before sending.</p>
              </div>
              <button
                onClick={() => setReminderPreview(null)}
                data-testid="close-reminder-preview"
                aria-label="Close preview"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', color: 'var(--text-muted)' }}
              >
                <XCircle size={22} />
              </button>
            </div>

            <div style={{ padding: '16px 24px 0', display: 'grid', gridTemplateColumns: '110px 1fr', gap: '6px 14px', fontSize: '0.8125rem' }}>
              <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>From:</div>
              <div data-testid="preview-sender" style={{ color: 'var(--text-dark)' }}>{reminderPreview.sender || '—'}</div>
              <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>To:</div>
              <div data-testid="preview-recipient" style={{ color: 'var(--text-dark)' }}>{reminderPreview.to_name ? `${reminderPreview.to_name} <${reminderPreview.to}>` : reminderPreview.to || '—'}</div>
              <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Subject:</div>
              <div data-testid="preview-subject" style={{ color: 'var(--text-dark)', fontWeight: 600 }}>{reminderPreview.subject || '—'}</div>
            </div>

            <div style={{ flex: 1, overflow: 'hidden', padding: '16px 24px', minHeight: '380px' }}>
              {reminderPreviewLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>Loading preview…</div>
              ) : reminderPreview.error ? (
                <div data-testid="preview-error" style={{ padding: '12px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', fontSize: '0.875rem', color: '#DC2626' }}>
                  {reminderPreviewError || 'Could not load preview.'}
                </div>
              ) : (
                <iframe
                  data-testid="preview-iframe"
                  title="Email preview"
                  srcDoc={reminderPreview.html}
                  sandbox=""
                  style={{ width: '100%', height: '100%', minHeight: '380px', border: '1px solid var(--border-color)', borderRadius: '6px', background: '#f5f5f5' }}
                />
              )}
            </div>

            {reminderPreviewError && !reminderPreview.error && (
              <div data-testid="preview-send-error" style={{ margin: '0 24px 12px', padding: '10px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', fontSize: '0.8125rem', color: '#DC2626' }}>
                {reminderPreviewError}
              </div>
            )}

            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setReminderPreview(null)}
                data-testid="cancel-reminder"
                style={{ padding: '10px 16px', background: '#fff', border: '1px solid var(--border-color)', color: 'var(--text-dark)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}
              >
                Close
              </button>
              {!reminderPreview.error && (
                <button
                  onClick={handleSendReminderConfirmed}
                  disabled={!!reminderInFlight[reminderPreview.profileId]}
                  data-testid="confirm-send-reminder"
                  style={{ padding: '10px 18px', background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)', cursor: reminderInFlight[reminderPreview.profileId] ? 'wait' : 'pointer', fontSize: '0.875rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: reminderInFlight[reminderPreview.profileId] ? 0.6 : 1 }}
                >
                  <AlertCircle size={14} /> {reminderInFlight[reminderPreview.profileId] ? 'Sending…' : 'Send Now'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ADMIN PROVIDER DETAIL PAGE
// ============================================
export const AdminProviderDetailPage = () => {
  const { id } = useParams();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  useEffect(() => {
    if (session?.access_token) fetchProvider();
  }, [session, id]);

  const fetchProvider = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/providers/${id}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) setProvider(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const url = `${API_URL}/admin/providers/${id}/${action}`;
      const opts = {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      };
      if (action === 'reject') opts.body = JSON.stringify({ reason: rejectReason });
      const res = await fetch(url, opts);
      let text = ''; try { text = await res.text(); } catch { text = ''; }
      let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = null; }
      if (res.ok) {
        setActionMsg({ type: 'success', text: data?.message || 'Done.' });
        setShowRejectForm(false);
        fetchProvider();
      } else {
        setActionMsg({ type: 'error', text: data?.detail || `Action failed (HTTP ${res.status})` });
      }
    } catch (err) {
      setActionMsg({ type: 'error', text: `Network error: ${err?.message || 'unknown'}` });
    }
    setActionLoading(false);
  };

  const [docActionBusyId, setDocActionBusyId] = useState(null);

  const handleDocApprove = async (docId) => {
    if (!window.confirm('Approve this document? The provider\'s verification badge will be updated accordingly.')) return;
    setDocActionBusyId(docId);
    setActionMsg(null);
    try {
      const res = await fetch(`${API_URL}/admin/documents/${docId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      let text = ''; try { text = await res.text(); } catch { text = ''; }
      let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = null; }
      if (res.ok) {
        setActionMsg({ type: 'success', text: data?.message || 'Document approved.' });
        fetchProvider();
      } else {
        setActionMsg({ type: 'error', text: data?.detail || `Approve failed (HTTP ${res.status})` });
      }
    } catch (err) {
      setActionMsg({ type: 'error', text: `Network error: ${err?.message || 'unknown'}` });
    }
    setDocActionBusyId(null);
  };

  const handleDocReject = async (docId) => {
    const reason = window.prompt('Reason for rejecting this document? (optional)', '') ?? null;
    if (reason === null) return; // cancelled
    setDocActionBusyId(docId);
    setActionMsg(null);
    try {
      const res = await fetch(`${API_URL}/admin/documents/${docId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      let text = ''; try { text = await res.text(); } catch { text = ''; }
      let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = null; }
      if (res.ok) {
        setActionMsg({ type: 'success', text: data?.message || 'Document rejected.' });
        fetchProvider();
      } else {
        setActionMsg({ type: 'error', text: data?.detail || `Reject failed (HTTP ${res.status})` });
      }
    } catch (err) {
      setActionMsg({ type: 'error', text: `Network error: ${err?.message || 'unknown'}` });
    }
    setDocActionBusyId(null);
  };

  const handleBulkApproveDocs = async () => {
    if (!window.confirm('Approve ALL pending documents for this provider? This will also update their verification badges.')) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const res = await fetch(`${API_URL}/admin/providers/${id}/approve-pending-documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      let text = ''; try { text = await res.text(); } catch { text = ''; }
      let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = null; }
      if (res.ok) {
        setActionMsg({ type: 'success', text: data?.message || 'Bulk approve complete.' });
        fetchProvider();
      } else {
        setActionMsg({ type: 'error', text: data?.detail || `Bulk approve failed (HTTP ${res.status})` });
      }
    } catch (err) {
      setActionMsg({ type: 'error', text: `Network error: ${err?.message || 'unknown'}` });
    }
    setActionLoading(false);
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!provider) return <div className="empty-state"><p>Provider not found</p></div>;

  const p = provider;
  const meta = p.metadata || {};

  const Section = ({ title, icon, children }) => (
    <div className="admin-detail-section">
      <h3>{icon} {title}</h3>
      {children}
    </div>
  );

  const Field = ({ label, value }) => (
    <div className="admin-field">
      <span className="admin-field-label">{label}</span>
      <span className="admin-field-value">{value || '—'}</span>
    </div>
  );

  return (
    <div className="page admin-page" data-testid="admin-provider-detail">
      <Link to="/admin/providers" className="admin-back-link" data-testid="back-to-list">
        <ChevronLeft size={18} /> Back to Provider List
      </Link>

      {/* Header */}
      <div className="admin-detail-header">
        <div>
          <h1>{p.business_name}</h1>
          <p style={{ color: 'var(--text-muted)' }}>{p.profile?.full_name} &middot; Registered {formatDateNZ(p.created_at)}</p>
        </div>
        <StatusBadge status={p.status} />
      </div>

      {/* Action bar */}
      {actionMsg && (
        <div data-testid="action-message" style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '0.875rem', background: actionMsg.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: actionMsg.type === 'success' ? '#065F46' : '#DC2626' }}>
          {actionMsg.text}
        </div>
      )}

      {(p.status === 'pending' || p.status === 'rejected') && (
        <div className="admin-action-bar" data-testid="admin-actions">
          <button
            className="btn-primary"
            onClick={() => handleAction('approve')}
            disabled={actionLoading}
            data-testid="approve-btn"
          >
            <CheckCircle size={16} /> {actionLoading ? 'Processing...' : 'Approve Provider'}
          </button>
          {p.status !== 'rejected' && !showRejectForm && (
            <button
              className="btn-outline"
              style={{ borderColor: '#DC2626', color: '#DC2626' }}
              onClick={() => setShowRejectForm(true)}
              data-testid="reject-toggle-btn"
            >
              <XCircle size={16} /> Reject Provider
            </button>
          )}
          {p.status === 'rejected' && (
            <button
              className="btn-outline"
              onClick={() => {
                if (window.confirm('Move this rejected provider back to Pending review?')) handleAction('reactivate');
              }}
              disabled={actionLoading}
              data-testid="reconsider-btn"
            >
              <Clock size={16} /> Move Back to Pending
            </button>
          )}
          {showRejectForm && (
            <div className="admin-reject-form" data-testid="reject-form">
              <textarea
                placeholder="Reason for rejection (optional)..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                data-testid="reject-reason-input"
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-primary" style={{ background: '#DC2626' }} onClick={() => handleAction('reject')} disabled={actionLoading} data-testid="confirm-reject-btn">
                  Confirm Rejection
                </button>
                <button className="btn-outline" onClick={() => setShowRejectForm(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {p.status === 'approved' && (
        <div className="admin-action-bar" data-testid="admin-actions-approved" style={{ marginBottom: '12px' }}>
          <button
            className="btn-outline"
            style={{ borderColor: '#DC2626', color: '#DC2626' }}
            onClick={() => {
              if (window.confirm('Suspend this provider?\n\nThey will be hidden from public listings immediately. Their Stripe subscription is NOT cancelled.')) handleAction('suspend');
            }}
            disabled={actionLoading}
            data-testid="suspend-btn"
          >
            <XCircle size={16} /> Suspend Provider
          </button>
        </div>
      )}

      {p.status === 'suspended' && (
        <div className="admin-action-bar" data-testid="admin-actions-suspended" style={{ marginBottom: '12px' }}>
          <button
            className="btn-primary"
            onClick={() => {
              if (window.confirm('Reactivate this provider and make them visible again?')) handleAction('reactivate');
            }}
            disabled={actionLoading}
            data-testid="reactivate-btn"
          >
            <CheckCircle size={16} /> {actionLoading ? 'Processing...' : 'Reactivate Provider'}
          </button>
        </div>
      )}

      {p.status === 'approved' && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '20px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: '#065F46' }}>
          <CheckCircle size={16} /> This provider is approved and publicly visible in listings.
        </div>
      )}

      {p.rejection_reason && p.status === 'rejected' && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: '20px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', fontSize: '0.875rem', color: '#DC2626' }}>
          <strong>Rejection reason:</strong> {p.rejection_reason}
        </div>
      )}

      <div className="admin-detail-grid">
        {/* Business Information */}
        <Section title="Business Information" icon={<Briefcase size={18} />}>
          <Field label="Business Name" value={p.business_name} />
          <Field label="Business Email" value={p.business_email} />
          <Field label="Business Phone" value={p.business_phone} />
          <Field label="Bio" value={p.bio} />
          <Field label="Years Experience" value={p.years_experience} />
          <Field label="Hourly Rate" value={p.hourly_rate_min && p.hourly_rate_max ? `$${p.hourly_rate_min} - $${p.hourly_rate_max}` : null} />
        </Section>

        {/* Contact Info */}
        <Section title="Account Owner" icon={<Mail size={18} />}>
          <Field label="Full Name" value={p.profile?.full_name} />
          <Field label="Email" value={p.profile?.email} />
          <Field label="Phone" value={p.profile?.phone} />
        </Section>

        {/* Service Area */}
        <Section title="Service Area & Availability" icon={<MapPin size={18} />}>
          <Field label="Country" value={p.country_code === 'NZ' ? 'New Zealand' : 'Australia'} />
          <Field label="City" value={p.city} />
          <Field label="Suburbs" value={p.suburbs_served?.join(', ')} />
          <Field label="Service Radius" value={meta.service_radius_km ? `${meta.service_radius_km} km` : null} />
          <Field label="Weekend Available" value={meta.weekend_available ? 'Yes' : 'No'} />
          <Field label="Same Day Available" value={meta.same_day_available ? 'Yes' : 'No'} />
        </Section>

        {/* Services */}
        <Section title={`Selected Services (${p.services?.length || 0})`} icon={<Star size={18} />}>
          {p.services?.length > 0 ? (
            <div className="admin-tags">
              {p.services.map((s, i) => (
                <span key={i} className="admin-tag">{s.name} <span style={{ color: 'var(--text-muted)' }}>({s.category})</span></span>
              ))}
            </div>
          ) : <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No services selected</p>}
        </Section>

        {/* Service Requests */}
        {p.service_requests?.length > 0 && (
          <Section title="Missing Service Requests" icon={<AlertCircle size={18} />}>
            {p.service_requests.map((r, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: i < p.service_requests.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{r.name}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{r.category} &middot; {r.description}</div>
              </div>
            ))}
          </Section>
        )}

        {/* Documents */}
        <Section title={`Verification Documents (${p.documents?.length || 0})`} icon={<FileText size={18} />}>
          {(() => {
            const pendingDocs = (p.documents || []).filter(d => (d.status || 'pending') === 'pending');
            if (pendingDocs.length === 0) return null;
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ fontSize: '0.8125rem', color: '#92400E', fontWeight: 600 }}>
                  <Clock size={13} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
                  {pendingDocs.length} pending document{pendingDocs.length > 1 ? 's' : ''} awaiting review
                </div>
                <button
                  type="button"
                  onClick={handleBulkApproveDocs}
                  disabled={actionLoading}
                  data-testid="bulk-approve-docs-btn"
                  style={{ padding: '6px 12px', background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  <CheckCircle size={12} /> {actionLoading ? 'Approving...' : `Approve All (${pendingDocs.length})`}
                </button>
              </div>
            );
          })()}
          {p.documents?.length > 0 ? p.documents.map((d, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < p.documents.length - 1 ? '1px solid var(--border-color)' : 'none', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'capitalize' }}>{d.verification_type?.replace('_', ' ')}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{d.file_name || 'Document'} &middot; {formatDateNZ(d.created_at)}</div>
                {d.rejection_reason && (d.status === 'rejected') && (
                  <div style={{ fontSize: '0.75rem', color: '#DC2626', marginTop: '4px' }}>
                    <strong>Reason:</strong> {d.rejection_reason}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="doc-view-btn"
                  onClick={async () => {
                    const r = await viewProviderDocument({
                      verificationId: d.id,
                      accessToken: session?.access_token,
                      apiUrl: API_URL,
                    });
                    if (!r.ok) alert(r.error);
                  }}
                  data-testid={`admin-view-doc-${d.id}`}
                >
                  <Eye size={13} /> View
                </button>
                {(d.status || 'pending') !== 'approved' && (
                  <button
                    type="button"
                    onClick={() => handleDocApprove(d.id)}
                    disabled={docActionBusyId === d.id}
                    data-testid={`admin-approve-doc-${d.id}`}
                    style={{ padding: '5px 10px', background: 'var(--success)', border: '1px solid var(--success)', color: '#fff', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <CheckCircle size={12} /> {docActionBusyId === d.id ? '...' : 'Approve'}
                  </button>
                )}
                {(d.status || 'pending') !== 'rejected' && (
                  <button
                    type="button"
                    onClick={() => handleDocReject(d.id)}
                    disabled={docActionBusyId === d.id}
                    data-testid={`admin-reject-doc-${d.id}`}
                    style={{ padding: '5px 10px', background: '#fff', border: '1px solid #DC2626', color: '#DC2626', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <XCircle size={12} /> Reject
                  </button>
                )}
                <StatusBadge status={d.status || 'pending'} />
              </div>
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No documents uploaded</p>}
        </Section>

        {/* Terms & Conditions Acceptance */}
        <Section title="Terms & Conditions" icon={<FileText size={18} />}>
          <div data-testid="terms-acceptance-block" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.875rem' }}>
            {p.accepted_terms ? (
              <>
                <CheckCircle size={18} style={{ color: 'var(--success)' }} />
                <span data-testid="terms-accepted-line">
                  Agreed to Terms on:{' '}
                  <strong>{p.accepted_terms_at ? formatDateTimeNZ(p.accepted_terms_at) : 'date not recorded'}</strong>
                </span>
              </>
            ) : (
              <>
                <XCircle size={18} style={{ color: '#DC2626' }} />
                <span data-testid="terms-not-accepted-line" style={{ color: '#DC2626' }}>
                  Not yet accepted Terms & Privacy Policy
                </span>
              </>
            )}
          </div>
        </Section>

        {/* Subscription */}
        <Section title="Subscription & Trial" icon={<Star size={18} />}>
          {p.subscription ? (
            <>
              <Field label="Plan" value={p.subscription.plan} />
              <Field label="Status" value={p.subscription.status} />
              <Field label="Stripe ID" value={p.subscription.stripe_subscription_id} />
              <Field label="Created" value={formatDateNZ(p.subscription.created_at)} />
            </>
          ) : <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No subscription</p>}
        </Section>
      </div>
    </div>
  );
};


// ============================================
// ADMIN CATEGORIES MANAGEMENT PAGE
// ============================================
const CategoryServicesPanel = ({ categoryId, categoryName, session, onChange }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service object or {new: true}
  const [form, setForm] = useState({ name: '', short_description: '', price_display: '', pricing_type: 'hourly', is_active: true, is_featured: false });
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const PRICING_OPTIONS = [
    { value: 'hourly', label: 'Hourly rate' },
    { value: 'quote', label: 'Quote on request' },
  ];

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/categories/${categoryId}/services`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) setServices(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchServices(); /* eslint-disable-next-line */ }, [categoryId]);

  const startNew = () => {
    setEditing({ new: true });
    setForm({ name: '', short_description: '', price_display: '', pricing_type: 'hourly', is_active: true, is_featured: false });
    setErrMsg('');
  };
  const startEdit = (svc) => {
    setEditing(svc);
    setForm({
      name: svc.name || '',
      short_description: svc.short_description || '',
      price_display: svc.price_display || '',
      pricing_type: svc.pricing_type || 'hourly',
      is_active: !!svc.is_active,
      is_featured: !!svc.is_featured,
    });
    setErrMsg('');
  };
  const cancel = () => { setEditing(null); setErrMsg(''); };

  const save = async () => {
    if (!form.name.trim()) { setErrMsg('Name is required'); return; }
    setSaving(true);
    setErrMsg('');
    try {
      const isNew = editing?.new;
      const url = isNew ? `${API_URL}/admin/services` : `${API_URL}/admin/services/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const body = isNew ? { ...form, category_id: categoryId } : form;
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const text = await res.text();
      let data = {}; try { data = text ? JSON.parse(text) : {}; } catch {}
      if (res.ok) {
        setEditing(null);
        await fetchServices();
        if (onChange) onChange();
      } else {
        setErrMsg(data?.detail || 'Save failed');
      }
    } catch (err) {
      setErrMsg('Network error');
    }
    setSaving(false);
  };

  const handleDelete = async (svc) => {
    if (!window.confirm(`Permanently delete "${svc.name}"? This cannot be undone.`)) return;
    setErrMsg('');
    try {
      const res = await fetch(`${API_URL}/admin/services/${svc.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const text = await res.text();
      let data = {}; try { data = text ? JSON.parse(text) : {}; } catch {}
      if (res.ok) {
        await fetchServices();
        if (onChange) onChange();
      } else {
        setErrMsg(data?.detail || 'Delete failed');
      }
    } catch (err) { setErrMsg('Network error'); }
  };

  const toggleActive = async (svc) => {
    try {
      await fetch(`${API_URL}/admin/services/${svc.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !svc.is_active })
      });
      fetchServices();
      if (onChange) onChange();
    } catch { /* ignore */ }
  };

  return (
    <div data-testid={`services-panel-content-${categoryId}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: '0.875rem' }}>Services in {categoryName} ({services.length})</strong>
        {!editing && (
          <button onClick={startNew} className="btn-primary" data-testid="add-service-btn"
            style={{ padding: '6px 14px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Plus size={12} /> Add Service
          </button>
        )}
      </div>

      {errMsg && <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#DC2626', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', marginBottom: 10 }}>{errMsg}</div>}

      {editing && (
        <div style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: 14, marginBottom: 12 }} data-testid="service-form">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Service Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Car Window Tinting" data-testid="svc-name-input"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>From Price (display)</label>
              <input type="text" value={form.price_display} onChange={e => setForm(f => ({ ...f, price_display: e.target.value }))}
                placeholder="e.g. $250-$600" data-testid="svc-price-input"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Pricing Type</label>
              <select value={form.pricing_type} onChange={e => setForm(f => ({ ...f, pricing_type: e.target.value }))}
                data-testid="svc-pricing-select"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem' }}>
                {PRICING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Description</label>
            <textarea value={form.short_description} onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))}
              rows={2} placeholder="Brief description shown to customers" data-testid="svc-description-input"
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 12, fontSize: '0.75rem' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} data-testid="svc-active-check" /> Active
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} data-testid="svc-featured-check" /> Featured
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="svc-save-btn" style={{ padding: '6px 14px', fontSize: '0.75rem' }}>
              {saving ? 'Saving…' : (editing.new ? 'Create Service' : 'Update Service')}
            </button>
            <button onClick={cancel} className="btn-outline" style={{ padding: '6px 14px', fontSize: '0.75rem' }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: '0.8125rem' }}>Loading services…</div>
      ) : services.length === 0 ? (
        <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: '0.8125rem', fontStyle: 'italic' }}>
          No services yet. Click <strong>Add Service</strong> to create one.
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: '0.75rem', background: '#fff', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
          <thead>
            <tr style={{ background: 'var(--surface-hover)', borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 600 }}>Name</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 600 }}>Price</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 600 }}>Status</th>
              <th style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map(svc => (
              <tr key={svc.id} style={{ borderBottom: '1px solid var(--border-color)', opacity: svc.is_active ? 1 : 0.5 }} data-testid={`svc-row-${svc.slug}`}>
                <td style={{ padding: 8 }}>
                  <div style={{ fontWeight: 600 }}>{svc.name}</div>
                  {svc.short_description && <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginTop: 2 }}>{svc.short_description.slice(0, 80)}</div>}
                </td>
                <td style={{ padding: 8, fontFamily: 'monospace', fontSize: '0.7rem' }}>{svc.price_display || '—'}</td>
                <td style={{ padding: 8 }}>
                  <span style={{ padding: '2px 8px', borderRadius: 50, fontSize: '0.65rem', fontWeight: 600,
                    background: svc.is_active ? 'rgba(34,197,94,0.1)' : 'rgba(107,114,128,0.1)',
                    color: svc.is_active ? '#16A34A' : '#6B7280' }}>{svc.is_active ? 'Active' : 'Disabled'}</span>
                </td>
                <td style={{ padding: 8, textAlign: 'right' }}>
                  <button onClick={() => startEdit(svc)} data-testid={`edit-svc-${svc.slug}`}
                    style={{ padding: '3px 8px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer', fontSize: '0.7rem', marginRight: 4 }}>Edit</button>
                  <button onClick={() => toggleActive(svc)} data-testid={`toggle-svc-${svc.slug}`}
                    style={{ padding: '3px 8px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer', fontSize: '0.7rem', marginRight: 4 }}>
                    {svc.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={() => handleDelete(svc)} data-testid={`delete-svc-${svc.slug}`}
                    style={{ padding: '3px 8px', border: '1px solid #DC2626', color: '#DC2626', borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer', fontSize: '0.7rem' }}>
                    <Trash2 size={10} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export const AdminCategoriesPage = () => {
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', icon_name: 'sparkles', is_active: true });
  const [saving, setSaving] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') { navigate('/login'); return; }
    fetchCategories();
  }, [authLoading, isAuthenticated, profile]);

  const fetchCategories = async () => {
    try {
      const token = session?.access_token;
      if (!token) return;
      const res = await fetch(`${API_URL}/admin/categories`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setCategories(await res.json());
    } catch (err) { console.error(err); }
    finally { setDataLoaded(true); }
  };

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const openNew = () => {
    setEditingId(null);
    setFormData({ name: '', slug: '', icon_name: 'sparkles', is_active: true });
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const openEdit = (cat) => {
    setEditingId(cat.id);
    setFormData({ name: cat.name, slug: cat.slug, icon_name: cat.icon_name || 'sparkles', is_active: cat.is_active });
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug) return;
    setSaving(true);
    try {
      const token = session?.access_token;
      const url = editingId ? `${API_URL}/admin/categories/${editingId}` : `${API_URL}/admin/categories`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setShowForm(false);
        fetchCategories();
      }
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const toggleActive = async (cat) => {
    try {
      const token = session?.access_token;
      await fetch(`${API_URL}/admin/categories/${cat.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !cat.is_active })
      });
      fetchCategories();
    } catch (err) { console.error(err); }
  };

  if (authLoading || !dataLoaded) return <div className="loading">Loading...</div>;

  const activeCount = categories.filter(c => c.is_active).length;
  const totalServices = categories.reduce((sum, c) => sum + (c.services_count || 0), 0);

  const ICON_OPTIONS = [
    'sparkles','wrench','zap','hammer','leaf','home','tool','truck','paint-roller',
    'shield','brush','ruler','wind','scissors','circuit-board','lock','frame',
    'cloud-rain','waves','fence'
  ];

  return (
    <div className="admin-page" data-testid="admin-categories-page">
      <div style={{ marginBottom: 24 }}>
        <Link to="/admin/providers" style={{ fontSize: '0.8125rem', color: 'var(--amber)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', marginBottom: 12 }}>
          <ChevronLeft size={14} /> Back to Admin
        </Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 4px' }}>Category Management</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{activeCount} active categories, {totalServices} total services</p>
          </div>
          <button className="btn-primary" onClick={openNew} data-testid="add-category-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> Add Category
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div ref={formRef} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px', marginBottom: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} data-testid="category-form">
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Category' : 'New Category'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => { setFormData(f => ({ ...f, name: e.target.value, slug: editingId ? f.slug : autoSlug(e.target.value) })); }}
                placeholder="e.g. Pool & Spa Services"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}
                data-testid="category-name-input"
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Slug</label>
              <input
                type="text"
                value={formData.slug}
                onChange={e => setFormData(f => ({ ...f, slug: e.target.value }))}
                placeholder="e.g. pool-spa-services"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}
                data-testid="category-slug-input"
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Icon</label>
            <select
              value={formData.icon_name}
              onChange={e => setFormData(f => ({ ...f, icon_name: e.target.value }))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}
              data-testid="category-icon-select"
            >
              {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={handleSave} disabled={saving || !formData.name} data-testid="save-category-btn">
              {saving ? 'Saving...' : editingId ? 'Update Category' : 'Create Category'}
            </button>
            <button className="btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Order</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Category</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Slug</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Icon</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: 'var(--text-muted)' }}>Services</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: 'var(--text-muted)' }}>Status</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => (
              <React.Fragment key={cat.id}>
              <tr style={{ borderBottom: '1px solid var(--border-color)', opacity: cat.is_active ? 1 : 0.5 }} data-testid={`cat-row-${cat.slug}`}>
                <td style={{ padding: '12px', color: 'var(--text-muted)' }}>{cat.sort_order}</td>
                <td style={{ padding: '12px' }}>
                  <strong>{cat.name}</strong>
                </td>
                <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{cat.slug}</td>
                <td style={{ padding: '12px', color: 'var(--text-muted)' }}>{cat.icon_name}</td>
                <td style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>{cat.services_count}</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 50, fontSize: '0.7rem', fontWeight: 600,
                    background: cat.is_active ? 'rgba(34,197,94,0.1)' : 'rgba(107,114,128,0.1)',
                    color: cat.is_active ? '#16A34A' : '#6B7280'
                  }}>
                    {cat.is_active ? <><Eye size={10} /> Active</> : <><EyeOff size={10} /> Hidden</>}
                  </span>
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setExpandedCategoryId(expandedCategoryId === cat.id ? null : cat.id)}
                      style={{ padding: '4px 10px', border: '1px solid var(--accent)', borderRadius: 'var(--radius-md)', background: expandedCategoryId === cat.id ? 'var(--accent)' : 'transparent', color: expandedCategoryId === cat.id ? '#fff' : 'var(--accent)', cursor: 'pointer', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}
                      data-testid={`manage-services-${cat.slug}`}
                    >
                      <Layers size={12} /> Services
                    </button>
                    <button
                      onClick={() => openEdit(cat)}
                      style={{ padding: '4px 10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'transparent', cursor: 'pointer', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      data-testid={`edit-${cat.slug}`}
                    >
                      <Edit2 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => toggleActive(cat)}
                      style={{ padding: '4px 10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'transparent', cursor: 'pointer', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4, color: cat.is_active ? '#DC2626' : '#16A34A' }}
                      data-testid={`toggle-${cat.slug}`}
                    >
                      {cat.is_active ? <><EyeOff size={12} /> Disable</> : <><Eye size={12} /> Enable</>}
                    </button>
                  </div>
                </td>
              </tr>
              {expandedCategoryId === cat.id && (
                <tr style={{ background: 'var(--surface-hover)' }} data-testid={`services-panel-${cat.slug}`}>
                  <td colSpan={7} style={{ padding: '14px 20px' }}>
                    <CategoryServicesPanel
                      categoryId={cat.id}
                      categoryName={cat.name}
                      session={session}
                      onChange={fetchCategories}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdminQuotesPage = () => {
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => { 
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchQuotes(); 
  }, [filter, authLoading, isAuthenticated, profile?.role, session?.access_token]);

  const fetchQuotes = async () => {
    try {
      const token = session?.access_token;
      if (!token) return;
      const url = filter ? `${API_URL}/admin/quotes?status=${filter}` : `${API_URL}/admin/quotes`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        setQuotes(await res.json());
      } else if (res.status === 403) {
        navigate('/');
      }
    } catch (err) { console.error('Admin quotes fetch error:', err); }
    finally { setDataLoaded(true); }
  };

  const statusStyles = {
    new: { bg: '#fef3c7', color: '#92400e', label: 'New' },
    sent: { bg: '#e0e7ff', color: '#3730a3', label: 'Sent' },
    responded: { bg: '#d1fae5', color: '#065f46', label: 'Responded' },
    closed: { bg: '#f3f4f6', color: '#374151', label: 'Closed' },
    canceled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
  };
  const defaultStatusStyle = { bg: '#f3f4f6', color: '#374151', label: 'Unknown' };

  if (authLoading || !dataLoaded) return <div className="loading">Loading...</div>;

  const counts = {
    all: quotes.length,
    new: quotes.filter(q => q.status === 'new').length,
    responded: quotes.filter(q => q.status === 'responded').length,
    unanswered: quotes.filter(q => q.responses_count === 0).length,
  };

  return (
    <div className="admin-page" data-testid="admin-quotes-page">
      <div style={{ marginBottom: 24 }}>
        <Link to="/admin" style={{ fontSize: '0.8125rem', color: 'var(--amber)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', marginBottom: 12 }}>
          <ChevronLeft size={14} /> Back to Admin Dashboard
        </Link>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '8px 0 4px' }}>Quote Requests</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Monitor customer quote activity</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', key: '', value: counts.all },
          { label: 'New', key: 'new', value: counts.new },
          { label: 'Responded', key: 'responded', value: counts.responded },
          { label: 'Unanswered', key: 'unanswered', value: counts.unanswered },
        ].map(s => (
          <button key={s.label} onClick={() => setFilter(s.key)} data-testid={`stat-${s.label.toLowerCase()}`}
            style={{ padding: '14px 16px', background: filter === s.key ? 'var(--navy)' : '#fff', color: filter === s.key ? '#fff' : 'var(--text-dark)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{s.value}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{s.label}</div>
          </button>
        ))}
      </div>

      {/* Table */}
      {quotes.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Service</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Customer</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Location</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Providers</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Responses</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map(q => {
                const ss = statusStyles[q.status] || defaultStatusStyle;
                const loc = [q.suburb, q.city].filter(Boolean).join(', ') || q.country_code || '—';
                return (
                  <tr key={q.id} style={{ borderBottom: '1px solid var(--border)' }} data-testid={`admin-quote-${q.id}`}>
                    <td style={{ padding: '12px' }}><strong>{q.service_name}</strong></td>
                    <td style={{ padding: '12px' }}>{q.customer_name}<br /><span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{q.customer_email}</span></td>
                    <td style={{ padding: '12px' }} data-testid={`admin-quote-location-${q.id}`}>{loc}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{q.providers_matched}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }} data-testid={`responses-${q.id}`}>
                      <span style={{
                        display: 'inline-block',
                        minWidth: '28px',
                        padding: '2px 8px',
                        borderRadius: '50px',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        background: q.responses_count === 0 ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.12)',
                        color: q.responses_count === 0 ? '#DC2626' : '#16A34A',
                      }}>{q.responses_count}</span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600, background: ss.bg, color: ss.color }}>{ss.label}</span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatDateNZ(q.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <p>No quote requests yet.</p>
        </div>
      )}
    </div>
  );
};


// ============================================
// ADMIN: RESET TEST ENVIRONMENT PAGE
// ============================================
export const AdminResetPage = () => {
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') { navigate('/login'); return; }
  }, [authLoading, isAuthenticated, profile]);

  const runPreview = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    setConfirmText('');
    try {
      const token = session?.access_token;
      const res = await fetch(`${API_URL}/admin/reset/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setPreview(await res.json());
      } else {
        setError('Failed to run preview');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const executeReset = async () => {
    if (confirmText !== 'RESET TEST DATA') return;
    setExecuting(true);
    setError('');
    try {
      const token = session?.access_token;
      const res = await fetch(`${API_URL}/admin/reset/execute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation_code: confirmText })
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setPreview(null);
        setConfirmText('');
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Execution failed');
      }
    } catch (err) {
      setError(err.message);
    }
    setExecuting(false);
  };

  if (authLoading) return <div className="loading">Loading...</div>;

  const CountRow = ({ label, count, highlight }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: '0.875rem', color: highlight && count > 0 ? '#DC2626' : 'var(--text-dark)' }}>{count}</span>
    </div>
  );

  return (
    <div className="admin-page" data-testid="admin-reset-page">
      <div style={{ marginBottom: 24 }}>
        <Link to="/admin/providers" style={{ fontSize: '0.8125rem', color: 'var(--amber)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', marginBottom: 12 }}>
          <ChevronLeft size={14} /> Back to Admin
        </Link>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '8px 0 4px' }}>Reset Test Environment</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Safely remove test/demo data without affecting production records.</p>
      </div>

      {/* Detection rules info */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 20 }}>
        <h3 style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Shield size={14} /> Test Detection Rules
        </h3>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Accounts are identified as test data if their email contains: <code style={{ background: 'rgba(245,158,11,0.1)', padding: '2px 6px', borderRadius: 4, fontSize: '0.7rem' }}>test, demo, example, temp, @testmail.com, unverified_, e2e_, fake_</code>
        </p>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
          <strong>Protected:</strong> Admin accounts, categories, services, and any accounts with non-test emails.
        </p>
      </div>

      {/* Step 1: Preview */}
      {!result && (
        <div style={{ marginBottom: 24 }}>
          <button className="btn-primary" onClick={runPreview} disabled={loading} data-testid="preview-reset-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} />
            {loading ? 'Scanning...' : 'Preview Test Reset'}
          </button>
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 'var(--radius-md)', padding: '12px 16px', color: '#DC2626', fontSize: '0.8125rem', marginBottom: 16 }}>
          <AlertCircle size={14} style={{ display: 'inline', marginRight: 4 }} /> {error}
        </div>
      )}

      {/* Preview Results */}
      {preview && !result && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px', marginBottom: 20 }} data-testid="reset-preview">
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 4, color: 'var(--text-dark)' }}>Dry Run Results</h3>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 16 }}>The following test data was detected and would be deleted:</p>

          <CountRow label="Test Profiles (auth users)" count={preview.profiles?.count || 0} highlight />
          <CountRow label="Test Providers" count={preview.providers?.count || 0} highlight />
          <CountRow label="Test Subscriptions" count={preview.subscriptions?.count || 0} highlight />
          <CountRow label="Provider Services" count={preview.provider_services?.count || 0} />
          <CountRow label="Quote Requests" count={preview.quote_requests?.count || 0} />
          <CountRow label="Quote Responses" count={preview.quote_responses?.count || 0} />
          <CountRow label="Quote-Provider Links" count={preview.quote_request_providers?.count || 0} />
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700, fontSize: '0.9375rem', color: '#DC2626' }}>
            <span>Total Records to Delete</span>
            <span>{preview.total_records}</span>
          </div>

          {/* Detail tables */}
          {preview.profiles?.details?.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>Test Accounts Detected:</h4>
              {preview.profiles.details.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, fontSize: '0.75rem', padding: '4px 0', color: 'var(--text-muted)' }}>
                  <span style={{ fontFamily: 'monospace' }}>{p.email}</span>
                  <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{p.role}</span>
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confirmation */}
          {preview.total_records > 0 && (
            <div style={{ marginTop: 24, padding: '16px', background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: 'var(--radius-md)' }} data-testid="reset-confirm-section">
              <h4 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#DC2626', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={16} /> Confirm Deletion
              </h4>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                Type <strong>RESET TEST DATA</strong> below to confirm deletion of {preview.total_records} records. This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="Type RESET TEST DATA"
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontFamily: 'monospace' }}
                  data-testid="reset-confirm-input"
                />
                <button
                  onClick={executeReset}
                  disabled={confirmText !== 'RESET TEST DATA' || executing}
                  style={{
                    padding: '10px 20px', borderRadius: 'var(--radius-md)', border: 'none', fontSize: '0.8125rem', fontWeight: 700, cursor: confirmText === 'RESET TEST DATA' ? 'pointer' : 'not-allowed',
                    background: confirmText === 'RESET TEST DATA' ? '#DC2626' : '#ccc',
                    color: 'white', display: 'inline-flex', alignItems: 'center', gap: 6
                  }}
                  data-testid="execute-reset-btn"
                >
                  {executing ? 'Deleting...' : 'Execute Reset'}
                </button>
              </div>
            </div>
          )}

          {preview.total_records === 0 && (
            <div style={{ marginTop: 16, padding: '12px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem', color: '#16A34A' }}>
              <CheckCircle size={14} style={{ display: 'inline', marginRight: 4 }} /> No test data found. Environment is clean.
            </div>
          )}
        </div>
      )}

      {/* Execution Results */}
      {result && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 'var(--radius-md)', padding: '20px' }} data-testid="reset-result">
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#16A34A', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={18} /> Reset Complete
          </h3>
          <CountRow label="Profiles Deleted" count={result.deleted?.profiles || 0} />
          <CountRow label="Auth Users Deleted" count={result.deleted?.auth_users || 0} />
          <CountRow label="Providers Deleted" count={result.deleted?.providers || 0} />
          <CountRow label="Subscriptions Deleted" count={result.deleted?.subscriptions || 0} />
          <CountRow label="Provider Services Deleted" count={result.deleted?.provider_services || 0} />
          <CountRow label="Quote Requests Deleted" count={result.deleted?.quote_requests || 0} />
          <CountRow label="Quote Responses Deleted" count={result.deleted?.quote_responses || 0} />
          <CountRow label="Quote-Provider Links Deleted" count={result.deleted?.quote_request_providers || 0} />
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700, fontSize: '0.9375rem', color: '#16A34A' }}>
            <span>Total Deleted</span>
            <span>{result.total_deleted}</span>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
            Triggered by: {result.triggered_by} at {formatDateTimeNZ(result.timestamp)}
          </p>
          <button className="btn-primary" onClick={() => { setResult(null); setPreview(null); }} style={{ marginTop: 16 }} data-testid="reset-again-btn">
            Run Another Preview
          </button>
        </div>
      )}
    </div>
  );
};


// ============================================
// ADMIN: PLATFORM OVERVIEW (homepage)
// ============================================
export const AdminOverviewPage = () => {
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') { navigate('/login'); return; }
    fetchOverview();
  }, [authLoading, isAuthenticated, profile]);

  const fetchOverview = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/overview`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error(err); }
    finally { setDataLoaded(true); }
  };

  if (authLoading || !dataLoaded) return <div className="loading">Loading...</div>;
  if (!stats) return <div className="empty-state"><p>Unable to load overview.</p></div>;

  const cards = [
    { label: 'Total Providers', value: stats.total_providers, icon: <Users size={18} />, link: '/admin/providers', testid: 'card-total-providers' },
    { label: 'Active Providers', value: stats.active_providers, icon: <CheckCircle size={18} />, link: '/admin/providers?status=approved', testid: 'card-active-providers', sub: `${stats.pending_providers} pending · ${stats.suspended_providers} suspended` },
    { label: 'Total Customers', value: stats.total_customers, icon: <Users size={18} />, link: '/admin/customers', testid: 'card-total-customers' },
    { label: 'Total Quote Requests', value: stats.total_quote_requests, icon: <FileText size={18} />, link: '/admin/quotes', testid: 'card-total-quotes' },
    { label: 'Active Subscriptions', value: stats.active_subscriptions, icon: <CreditCard size={18} />, link: '/admin/subscriptions', testid: 'card-active-subs', sub: `${stats.trial_subscriptions} trial · ${stats.past_due_subscriptions} past due` },
    { label: 'Total Reviews', value: stats.total_reviews, icon: <Star size={18} />, link: '/admin/reviews', testid: 'card-total-reviews' },
  ];

  const shortcuts = [
    { label: 'Provider Management', link: '/admin/providers', icon: <Users size={16} /> },
    { label: 'Customers', link: '/admin/customers', icon: <Users size={16} /> },
    { label: 'Quote Requests', link: '/admin/quotes', icon: <FileText size={16} /> },
    { label: 'Subscriptions', link: '/admin/subscriptions', icon: <CreditCard size={16} /> },
    { label: 'Review Moderation', link: '/admin/reviews', icon: <MessageSquare size={16} /> },
    { label: 'Categories', link: '/admin/categories', icon: <Layers size={16} /> },
    { label: 'Reset Test Data', link: '/admin/reset', icon: <AlertTriangle size={16} /> },
  ];

  return (
    <div className="page admin-page" data-testid="admin-overview-page">
      <div className="page-header">
        <h1>Admin Dashboard</h1>
        <p>Platform overview and quick actions</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 28 }}>
        {cards.map(c => {
          const Card = (
            <div data-testid={c.testid} style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '18px 20px', cursor: c.link ? 'pointer' : 'default', height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: 6 }}>
                {c.icon}<span>{c.label}</span>
              </div>
              <div style={{ fontSize: '1.875rem', fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1.1 }}>{c.value}</div>
              {c.sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>{c.sub}</div>}
            </div>
          );
          return c.link ? <Link key={c.label} to={c.link} style={{ textDecoration: 'none' }}>{Card}</Link> : <div key={c.label}>{Card}</div>;
        })}
      </div>

      <div style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '18px 20px' }}>
        <h2 style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <BarChart3 size={16} /> Quick Actions
        </h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {shortcuts.map(s => (
            <Link key={s.label} to={s.link} data-testid={`shortcut-${s.link}`}
              style={{ padding: '8px 14px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-sm)', textDecoration: 'none', color: 'var(--text-dark)', fontSize: '0.8125rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {s.icon}{s.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// ADMIN: SUBSCRIPTION MANAGEMENT
// ============================================
export const AdminSubscriptionsPage = () => {
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') { navigate('/login'); return; }
    fetchSubs();
  }, [filter, authLoading, isAuthenticated, profile]);

  const fetchSubs = async () => {
    try {
      const url = filter ? `${API_URL}/admin/subscriptions?status=${filter}` : `${API_URL}/admin/subscriptions`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.ok) setSubs(await res.json());
    } catch (err) { console.error(err); }
    finally { setDataLoaded(true); }
  };

  if (authLoading || !dataLoaded) return <div className="loading">Loading...</div>;

  const counts = {
    all: subs.length,
    active: subs.filter(s => s.status === 'active').length,
    trial: subs.filter(s => s.status === 'trial').length,
    past_due: subs.filter(s => s.status === 'past_due').length,
  };
  const visible = filter ? subs.filter(s => s.status === filter) : subs;

  const StatusPill = ({ status }) => {
    const ss = SUB_STATUS_STYLES[status] || { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: status };
    return <span data-testid={`sub-status-${status}`} style={{ padding: '2px 10px', borderRadius: '50px', fontSize: '0.7rem', fontWeight: 600, background: ss.bg, color: ss.color }}>{ss.label}</span>;
  };

  return (
    <div className="page admin-page" data-testid="admin-subscriptions-page">
      <div className="page-header">
        <Link to="/admin" style={{ fontSize: '0.8125rem', color: 'var(--amber)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', marginBottom: 12 }}>
          <ChevronLeft size={14} /> Back to Admin Dashboard
        </Link>
        <h1>Subscription Management</h1>
        <p>All provider subscriptions across the platform</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'All', key: '', value: counts.all },
          { label: 'Active', key: 'active', value: counts.active },
          { label: 'Trial', key: 'trial', value: counts.trial },
          { label: 'Past Due', key: 'past_due', value: counts.past_due },
        ].map(s => (
          <button key={s.label} onClick={() => setFilter(s.key)} data-testid={`sub-filter-${s.key || 'all'}`}
            style={{ padding: '14px 16px', background: filter === s.key ? 'var(--navy)' : '#fff', color: filter === s.key ? '#fff' : 'var(--text-dark)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{s.value}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{s.label}</div>
          </button>
        ))}
      </div>

      <div className="admin-table-container" data-testid="subscriptions-table">
        {visible.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <CreditCard size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <p>No subscriptions found</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Trial Ends</th>
                <th>Stripe Reference</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(s => (
                <tr key={s.id} data-testid={`sub-row-${s.id}`}>
                  <td>
                    <div className="admin-cell-primary">{s.provider_name || '—'}</div>
                    <div className="admin-cell-secondary">{s.provider_email} · {s.provider_city}, {s.provider_country}</div>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{s.plan || '—'}</td>
                  <td><StatusPill status={s.status} /></td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.trial_end ? formatDateNZ(s.trial_end) : '—'}</td>
                  <td style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {s.stripe_subscription_id || <span style={{ color: '#DC2626' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============================================
// ADMIN: REVIEW MODERATION
// ============================================
export const AdminReviewsPage = () => {
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [lowOnly, setLowOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') { navigate('/login'); return; }
    fetchReviews();
  }, [lowOnly, statusFilter, authLoading, isAuthenticated, profile]);

  const fetchReviews = async () => {
    try {
      const params = new URLSearchParams();
      if (lowOnly) params.set('low_rating', 'true');
      if (statusFilter) params.set('status', statusFilter);
      const url = `${API_URL}/admin/reviews${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.ok) setReviews(await res.json());
    } catch (err) { console.error(err); }
    finally { setDataLoaded(true); }
  };

  const handleHide = async (id) => {
    setActionMsg(null);
    try {
      const res = await fetch(`${API_URL}/admin/reviews/${id}/hide`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) { setActionMsg({ type: 'success', text: 'Review hidden.' }); fetchReviews(); }
      else setActionMsg({ type: 'error', text: 'Failed to hide review.' });
    } catch { setActionMsg({ type: 'error', text: 'Network error.' }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this review? This cannot be undone.')) return;
    setActionMsg(null);
    try {
      const res = await fetch(`${API_URL}/admin/reviews/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) { setActionMsg({ type: 'success', text: 'Review deleted.' }); fetchReviews(); }
      else setActionMsg({ type: 'error', text: 'Failed to delete review.' });
    } catch { setActionMsg({ type: 'error', text: 'Network error.' }); }
  };

  if (authLoading || !dataLoaded) return <div className="loading">Loading...</div>;

  const ReviewStatusPill = ({ status }) => {
    const ss = REVIEW_STATUS_STYLES[status] || { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: status };
    return <span data-testid={`rev-status-${status}`} style={{ padding: '2px 10px', borderRadius: '50px', fontSize: '0.7rem', fontWeight: 600, background: ss.bg, color: ss.color }}>{ss.label}</span>;
  };

  return (
    <div className="page admin-page" data-testid="admin-reviews-page">
      <div className="page-header">
        <Link to="/admin" style={{ fontSize: '0.8125rem', color: 'var(--amber)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', marginBottom: 12 }}>
          <ChevronLeft size={14} /> Back to Admin Dashboard
        </Link>
        <h1>Review Moderation</h1>
        <p>{reviews.length} review{reviews.length !== 1 ? 's' : ''} {lowOnly ? '(1–2 stars)' : ''}{statusFilter ? ` · ${statusFilter}` : ''}</p>
      </div>

      {actionMsg && (
        <div data-testid="rev-action-msg" style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', marginBottom: 14, fontSize: '0.8125rem', background: actionMsg.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: actionMsg.type === 'success' ? '#065F46' : '#DC2626' }}>
          {actionMsg.text}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setLowOnly(false)} data-testid="filter-rev-all"
          style={{ padding: '8px 14px', background: !lowOnly ? 'var(--navy)' : '#fff', color: !lowOnly ? '#fff' : 'var(--text-dark)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
          All ratings
        </button>
        <button onClick={() => setLowOnly(true)} data-testid="filter-rev-low"
          style={{ padding: '8px 14px', background: lowOnly ? '#DC2626' : '#fff', color: lowOnly ? '#fff' : 'var(--text-dark)', border: '1px solid #DC2626', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <AlertTriangle size={13} /> Low ratings (1–2★)
        </button>
        <span style={{ width: 1, background: 'var(--border-color)', margin: '0 4px' }} />
        {['', 'published', 'hidden', 'flagged'].map(s => (
          <button key={s || 'all-status'} onClick={() => setStatusFilter(s)} data-testid={`filter-rev-status-${s || 'all'}`}
            style={{ padding: '8px 12px', background: statusFilter === s ? 'var(--navy)' : '#fff', color: statusFilter === s ? '#fff' : 'var(--text-dark)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
            {s || 'Any status'}
          </button>
        ))}
      </div>

      {reviews.length === 0 ? (
        <div className="empty-state" style={{ padding: '48px 24px', textAlign: 'center' }}>
          <MessageSquare size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <p>No reviews match your filters</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reviews.map(r => (
            <div key={r.id} data-testid={`rev-row-${r.id}`} style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '14px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{r.provider_name || '—'} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· {r.provider_city}</span></div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>From: {r.customer_name} · {formatDateNZ(r.created_at)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 1 }}>
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={14} fill={i <= r.rating ? '#f59e0b' : 'none'} stroke={i <= r.rating ? '#f59e0b' : '#d1d5db'} />
                    ))}
                  </div>
                  <ReviewStatusPill status={r.status} />
                </div>
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-dark)', lineHeight: 1.5, marginBottom: 10 }}>{r.comment || <em style={{ color: 'var(--text-muted)' }}>(no comment)</em>}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {r.status !== 'hidden' && (
                  <button onClick={() => handleHide(r.id)} data-testid={`hide-rev-${r.id}`}
                    style={{ padding: '6px 12px', background: '#fff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <EyeOff size={12} /> Hide
                  </button>
                )}
                <button onClick={() => handleDelete(r.id)} data-testid={`delete-rev-${r.id}`}
                  style={{ padding: '6px 12px', background: '#fff', border: '1px solid #DC2626', color: '#DC2626', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
